import {yupResolver} from "@hookform/resolvers/yup";
import classNames from "classnames";
import {useEffect, useMemo, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";

import Button from "@/components/button/Button.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {SelectServiceBoxes} from "@/components/form/SelectServiceBoxes.tsx";
import {dynamicTabConfig, staticTabConfig, TabConfig} from "@/components/layout/TabContentContainer.types.ts";
import {TabForm} from "@/components/TabForm/TabForm.tsx";
import {CheckUnsavedModal} from "@/pages/checks/update/sections/CheckUnsavedModal.tsx";
import {
  MultiServiceTarget,
  SLAReportFormProps,
  SLAReportFormTabs,
  SLAReportFormValues,
} from "@/pages/reports/sla/SLAReportForm.types.ts";
import {
  checksField,
  defaultSLAReportFormConfig,
  getSLAReportFormDefaultValues,
  groupInEditChecksField,
  groupInEditNameField,
  SLAReportFormValuesSchema,
  SLAReportingGroupsSectionSchema,
  SLAReportSettingsSectionSchema,
} from "@/pages/reports/sla/SLAReportForm.utils.tsx";
import {SLAReportingGroupsSection} from "@/pages/reports/sla/SLAReportingGroupsSection.tsx";
import {SLAReportSettingsSection} from "@/pages/reports/sla/SLAReportSettingsSection.tsx";

export const SLAReportForm = ({
  title,
  formId,
  onSubmit,
  onCancel,
  tags,
  monitoringServiceTypes,
  serverErrors,
  slaReport,
  slaReportGlobalLogoUrl,
}: SLAReportFormProps) => {
  const [tab, setTab] = useState(SLAReportFormTabs.GLOBAL as string);
  const [tabState] = useState<Record<SLAReportFormTabs, dynamicTabConfig>>(defaultSLAReportFormConfig);
  const [showSubForm, setShowSubForm] = useState(false);
  const [saveConfirm, setSaveConfirm] = useState<SLAReportFormValues | null>(null);
  const [multiServiceTarget, setMultiServiceTarget] = useState<MultiServiceTarget>(MultiServiceTarget.Report);
  const methods = useForm<SLAReportFormValues>({
    resolver: yupResolver<SLAReportFormValues>(SLAReportFormValuesSchema),
    defaultValues: getSLAReportFormDefaultValues(slaReport),
  });
  const {
    handleSubmit,
    watch,
    setError,
    formState: {errors},
  } = methods;
  const groupInEditName = watch(groupInEditNameField);
  const groupInEditChecks = watch(groupInEditChecksField);

  useEffect(() => {
    if (serverErrors) {
      Object.entries(serverErrors).forEach(([field, messages]) => {
        if (field !== "__all__") {
          setError(field as keyof SLAReportFormValues, {
            message: Array.isArray(messages) ? messages.join("\n") : String(messages),
          });
        }
      });
    }
  }, [serverErrors, setError]);

  const SLAReportFormTabConfig: Record<SLAReportFormTabs, staticTabConfig> = useMemo(
    () => ({
      global: {
        id: "global",
        title: "Global",
        fieldList: Object.keys(SLAReportSettingsSectionSchema.describe().fields),
        html: () => (
          <div className={classNames({"d-none": showSubForm})}>
            <SLAReportSettingsSection
              toggleSubform={() => {
                setMultiServiceTarget(MultiServiceTarget.Report);
                setShowSubForm(true);
              }}
              tags={tags}
              slaReportGlobalLogoUrl={slaReportGlobalLogoUrl}
            />
          </div>
        ),
      },
      reportingGroups: {
        id: "reportingGroups",
        title: "Reporting Groups",
        fieldList: Object.keys(SLAReportingGroupsSectionSchema.describe().fields),
        html: () => (
          <div className={classNames({"d-none": showSubForm})}>
            <SLAReportingGroupsSection
              toggleFieldSelector={() => {
                setMultiServiceTarget(MultiServiceTarget.GroupInEdit);
                setShowSubForm(true);
              }}
              defaultGroups={slaReport?.groups || []}
            />
          </div>
        ),
      },
    }),
    [showSubForm, slaReport?.groups, slaReportGlobalLogoUrl, tags]
  );

  const sections: Record<string, TabConfig> = useMemo(() => {
    const derivedSections: Record<string, TabConfig> = {...SLAReportFormTabConfig};
    Object.keys(derivedSections).forEach((key) => {
      const tabKey = key as SLAReportFormTabs;
      derivedSections[tabKey] = {
        ...derivedSections[tabKey],
        ...tabState[tabKey],
      };
    });
    return derivedSections;
  }, [SLAReportFormTabConfig, tabState]);

  const SLAReportFormFieldToTabMap: Record<string, SLAReportFormTabs> = useMemo(
    () =>
      Object.values(SLAReportFormTabConfig).reduce((acc: Record<string, SLAReportFormTabs>, section) => {
        if (section.fieldList) {
          section.fieldList.forEach((field: string) => {
            acc[field] = section.id as SLAReportFormTabs;
          });
        }
        return acc;
      }, {}),
    [SLAReportFormTabConfig]
  );

  return (
    <>
      <CheckUnsavedModal
        show={!!saveConfirm}
        title="You have unsaved Reporting Groups"
        body="Are you sure you want to save the form? Group data might be lost."
        confirmText="Save"
        onConfirm={() => onSubmit(saveConfirm as SLAReportFormValues)}
        onCancel={() => setSaveConfirm(null)}
      />
      <FormProvider {...methods}>
        <TabForm
          title={title}
          tab={tab}
          setTab={setTab}
          sections={sections}
          variant="modal"
          formId={formId}
          submitText="Save"
          fieldToTabMap={SLAReportFormFieldToTabMap}
          goBack={onCancel}
          actionsBar={
            showSubForm && <Button variant="secondary" onClick={() => setShowSubForm(false)}>{`<< OK`}</Button>
          }
          headerClassName={showSubForm ? "d-none" : undefined}
          serverErrorsState={serverErrors}
        >
          <form
            id={formId}
            onSubmit={handleSubmit((data) => {
              if (groupInEditName || groupInEditChecks?.length) {
                setSaveConfirm(data);
                return;
              }
              return onSubmit(data);
            }, console.log)}
          >
            {(errors.show_response_time_section || errors.show_uptime_section) && (
              <FormErrorAlerts
                errors={["SLA Reports must include at least one section: Uptime or Response Time."]}
                className="mb-4"
              />
            )}
            {showSubForm && (
              <SelectServiceBoxes
                name={multiServiceTarget === MultiServiceTarget.Report ? checksField : groupInEditChecksField}
                monitoringServiceTypes={monitoringServiceTypes}
                tags={tags}
              />
            )}
            {sections[tab].html()}
          </form>
        </TabForm>
      </FormProvider>
    </>
  );
};
